import React from 'react';
import PropTypes from 'prop-types';

import { CharactersRemaining } from 'components/Form';
import { CurrencyInput, Input, Textarea } from 'components/HookForm';
import Message from 'components/Message';
import When from 'components/When';
import { URL_SIMPLE_REGEX } from 'constants/logic';
import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';

import {
  DESCRIPTION_INPUT_HEIGHT_IN_LINES,
  SKU_DESCRIPTION_MAX_INPUT_LENGTH,
  SKU_NAME_MAX_INPUT_LENGTH,
  SKU_NAME_CHAR_COUNTER_THRESHOLD,
  SKU_DESCRIPTION_CHAR_COUNTER_THRESHOLD,
} from '../SkuItems.constants';
import { validateCategories, validatePrice } from '../SkuItemForm.helpers';
import SkuCategoryDropdown from './SkuCategoryDropdown';

const { element } = bem('item-form');

const SkuItemFormFields = ({
  messages,
  groups,
  subgroups,
  currencyCode,
  categories,
  onCategoriesChange,
  categoriesError,
  onAddCategory,
  watchItemName,
  watchItemDescription,
  menuPriceDescription,
  itemNameProps,
  menuPriceProps,
  linkToMenuProps,
  priceVerificationSlot,
  isEditMode,
  disabled,
  showPricingFields,
  pricingOptional,
}) => (
  <>
    <Input
      name="itemName"
      label={messages.itemNameLabel}
      required
      showRequiredLabel={!disabled}
      maxLength={SKU_NAME_MAX_INPUT_LENGTH}
      disabled={disabled}
      {...itemNameProps}
    />
    <When predicate={SKU_NAME_MAX_INPUT_LENGTH - (watchItemName || '').length <= SKU_NAME_CHAR_COUNTER_THRESHOLD}>
      <div
        className={element('characters-remaining', {
          error: SKU_NAME_MAX_INPUT_LENGTH === (watchItemName || '').length,
        })}>
        <CharactersRemaining length={SKU_NAME_MAX_INPUT_LENGTH} value={watchItemName || ''} showMaxLength />
      </div>
    </When>

    <Textarea
      name="itemDescription"
      label={messages.itemDescriptionLabel}
      placeholder={messages.itemDescriptionPlaceholder}
      required
      showRequiredLabel={!disabled}
      maxLength={SKU_DESCRIPTION_MAX_INPUT_LENGTH}
      heightInLines={DESCRIPTION_INPUT_HEIGHT_IN_LINES}
      disabled={disabled}
    />
    <When
      predicate={
        SKU_DESCRIPTION_MAX_INPUT_LENGTH - (watchItemDescription || '').length <= SKU_DESCRIPTION_CHAR_COUNTER_THRESHOLD
      }>
      <div
        className={element('characters-remaining', {
          error: SKU_DESCRIPTION_MAX_INPUT_LENGTH === (watchItemDescription || '').length,
        })}>
        <CharactersRemaining
          length={SKU_DESCRIPTION_MAX_INPUT_LENGTH}
          value={watchItemDescription || ''}
          showMaxLength
        />
      </div>
    </When>

    <SkuCategoryDropdown
      name="categories"
      label={disabled && messages.categoryLabelReadOnly ? messages.categoryLabelReadOnly : messages.categoryLabel}
      groups={groups}
      subgroups={subgroups}
      value={categories}
      onChange={onCategoriesChange}
      validate={validateCategories(messages.categoryRequired)}
      required
      showRequiredLabel={!disabled}
      error={categoriesError}
      onAddCategory={onAddCategory}
      selectCategoryMessage={messages.selectCategory}
      addCategoryMessage={messages.addCategoryLabel}
      disabled={disabled}
    />

    {priceVerificationSlot}

    <When predicate={showPricingFields}>
      <When predicate={isEditMode}>
        <Message as="p" message={messages.pricingFieldsHelperText} className={element('input-description')} />
      </When>

      <CurrencyInput
        name="menuPrice"
        label={messages.menuPriceLabel}
        currencyCode={currencyCode}
        required={!isEditMode && !pricingOptional}
        showRequiredLabel={!isEditMode && !pricingOptional}
        validate={validatePrice(messages.priceMustBePositive)}
        disabled={disabled}
        {...menuPriceProps}
      />
      <Message as="p" message={menuPriceDescription} className={element('input-description')} />

      <Input
        name="linkToMenu"
        label={messages.linkToMenuLabel}
        placeholder={messages.linkToMenuPlaceholder}
        required={!isEditMode && !pricingOptional}
        showRequiredLabel={!isEditMode && !pricingOptional}
        pattern={{
          value: URL_SIMPLE_REGEX,
          message: messages.invalidUrl,
        }}
        disabled={disabled}
        {...linkToMenuProps}
      />
      <Message as="p" message={messages.linkToMenuDescription} className={element('input-description')} />
    </When>
  </>
);

SkuItemFormFields.propTypes = {
  messages: PropTypes.shape({
    itemNameLabel: intlMessage.isRequired,
    itemDescriptionLabel: intlMessage.isRequired,
    itemDescriptionPlaceholder: intlMessage.isRequired,
    categoryLabel: intlMessage.isRequired,
    categoryLabelReadOnly: intlMessage,
    categoryRequired: intlMessage.isRequired,
    selectCategory: intlMessage.isRequired,
    addCategoryLabel: intlMessage.isRequired,
    menuPriceLabel: intlMessage.isRequired,
    priceMustBePositive: intlMessage.isRequired,
    linkToMenuLabel: intlMessage.isRequired,
    linkToMenuPlaceholder: intlMessage.isRequired,
    invalidUrl: intlMessage.isRequired,
    linkToMenuDescription: intlMessage.isRequired,
    pricingFieldsHelperText: intlMessage,
  }).isRequired,
  groups: PropTypes.arrayOf(PropTypes.shape({})),
  subgroups: PropTypes.arrayOf(PropTypes.shape({})),
  currencyCode: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  onCategoriesChange: PropTypes.func.isRequired,
  categoriesError: PropTypes.shape({}),
  onAddCategory: PropTypes.func,
  watchItemName: PropTypes.string,
  watchItemDescription: PropTypes.string,
  menuPriceDescription: intlMessage.isRequired,
  itemNameProps: PropTypes.shape({}),
  menuPriceProps: PropTypes.shape({}),
  linkToMenuProps: PropTypes.shape({}),
  priceVerificationSlot: PropTypes.node,
  isEditMode: PropTypes.bool,
  disabled: PropTypes.bool,
  showPricingFields: PropTypes.bool,
  pricingOptional: PropTypes.bool,
};

SkuItemFormFields.defaultProps = {
  groups: [],
  subgroups: [],
  currencyCode: undefined,
  categories: [],
  categoriesError: undefined,
  onAddCategory: null,
  watchItemName: '',
  watchItemDescription: '',
  itemNameProps: {},
  menuPriceProps: {},
  linkToMenuProps: {},
  priceVerificationSlot: null,
  showPricingFields: true,
  pricingOptional: false,
};

export default SkuItemFormFields;
